/**
 * DataSource for authz.role_permission logical table
 * Tracks which objects each role grants access to
 */

import { DataSource, FilterSchema } from './DataSource';
import { queryGqlApi } from '../../graphql/queryGqlApi';
import { LIST_ROLE_PERMISSIONS, onCreateRolePermission, onUpdateRolePermission, onDeleteRolePermission } from '../../graphql/rbac/rolePermissions';
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {IGraphQLSubscriptionClient} from "@stores/gqlModel/IGraphQLSubscriptionClient";

export interface RolePermission {
    id: string;
    ownerId: string;
    roleId: string;
    workspaceId: string;
    objectType: string;
    objectId: string | null;
    createdAt: string;
    createdBy?: string;
}

export interface RolePermissionEntityFilters {
    libraryId: string | null;
    testId: string | null;
    locationId: string | null;
}

/**
 * DataSource for role permissions
 * Provides real-time sync via WebSocket subscriptions
 */
export class RolePermissionsDataSource extends DataSource<RolePermission, RolePermission, BasicDataSourceDeps> {
    readonly _modelName = 'RolePermission';
    readonly _subscriptionGQLs = {
        onCreate: onCreateRolePermission,
        onUpdate: onUpdateRolePermission,
        onDelete: onDeleteRolePermission,
    };
    
    async loadGQLFetch(): Promise<{ items: RolePermission[], nextToken: string | null }> {
        return queryGqlApi(
            LIST_ROLE_PERMISSIONS,
            { limit: 1000 },
            this.deps.dataClientService,
            this.deps.authService,
            'pgServer'
        );
    }
    
    async loadMoreGQLFetch(nextToken: string): Promise<{ items: RolePermission[], nextToken: string | null }> {
        return queryGqlApi(
            LIST_ROLE_PERMISSIONS,
            { limit: 1000, nextToken },
            this.deps.dataClientService,
            this.deps.authService,
            'pgServer'
        );
    }
    
    getWsClient(): Promise<IGraphQLSubscriptionClient | undefined> {
        return this.deps.dataClientService.getWsClientByKey('pgServer');
    }
    
    /**
     * Get all permissions for a specific role
     */
    getPermissionsByRoleId(roleId: string): RolePermission[] {
        return this.allRows.filter(rp => rp.roleId === roleId);
    }
    
    /**
     * Get all permissions for a specific workspace
     */
    getPermissionsByWorkspaceId(workspaceId: string): RolePermission[] {
        return this.allRows.filter(rp => rp.workspaceId === workspaceId);
    }
    
    /**
     * Get permissions for a specific object
     */
    getPermissionsByObject(objectType: string, objectId: string | null): RolePermission[] {
        return this.allRows.filter(
            rp => rp.objectType === objectType && rp.objectId === objectId
        );
    }
    
    /**
     * Check if a role has permission for a specific object
     */
    hasPermission(roleId: string, objectType: string, objectId: string | null): boolean {
        return this.allRows.some(
            rp => rp.roleId === roleId && rp.objectType === objectType && rp.objectId === objectId
        );
    }
    
    /**
     * Wildcard permission rows were removed. Future access now lives in Role.includeFutureConfig.
     */
    hasAllPermission(_roleId: string, _objectType: string): boolean {
        return false;
    }

    /**
     * Mock mode has no WS subscriptions (see wire-feature-to-demo skill), so after an
     * assign-to-roles mutation the DS must be updated locally or the UI stays stale until
     * a reload. Replaces every row for (objectType, objectId) with the given set.
     */
    replacePermissionsForObject(objectType: string, objectId: string, permissions: RolePermission[]) {
        const keepIds = new Set(permissions.map(p => p.id));
        this.allRows
            .filter(p => p.objectType === objectType && p.objectId === objectId && !keepIds.has(p.id))
            .forEach(p => this._removeItem(p.id));
        permissions.forEach(p => this._upsertItem(p));
    }
    
    getFilterSchema(): FilterSchema<RolePermission> {
        return {
            workspaceId: {
                type: 'client',
                apply: (permission: RolePermission) => (
                    this.filterValues.workspaceId === null
                    || permission.workspaceId === this.filterValues.workspaceId
                ),
            },
            entity: {
                type: 'client',
                apply: (permission: RolePermission) => {
                    const filters = this.filterValues.entity as RolePermissionEntityFilters | null | undefined;
                    if (!filters?.libraryId && !filters?.testId && !filters?.locationId) {
                        return true;
                    }

                    return (
                        (filters.libraryId !== null && permission.objectType === 'item' && permission.objectId === filters.libraryId)
                        || (filters.testId !== null && permission.objectType === 'quiz' && permission.objectId === filters.testId)
                        || (filters.locationId !== null && permission.objectType === 'task_list_unit' && permission.objectId === filters.locationId)
                    );
                },
            },
        };
    }
    
    getFilterDefaultValues() {
        return {
            workspaceId: null,
            entity: {
                libraryId: null,
                testId: null,
                locationId: null,
            } satisfies RolePermissionEntityFilters,
        };
    }
    
    get syncHash() {
        return '';
    }
}
